/**
 * What an alerted garrison does about the expansion's objectives (10 §3): the launchers drive
 * off, the staff burn the documents, the contact stays away, the general is hurried to his car,
 * the guards are told to shoot the scientist rather than lose him. `rules.ts` runs the clock;
 * this file says whether each effect can still happen, and what it does when its time comes.
 */

import { makeNoise } from '../alert';
import { type Ctx, isAlive, isFighter } from '../ctx';
import { damageEnemy } from '../damage';
import { isAutomated } from '../enemies/specials/kinds';
import { atan2, cos, sin } from '../math';
import { spawnBullet } from '../projectiles';
import { EXPANSION, NOISE } from '../tuning';
import type { AlertEffect, Enemy, ObjectiveRule } from '../types';
import { dist, failObjective, loseCaptive, propDestroyed, targetName, targetProps } from './common';
import { wakeTagged } from './dormant';
import { officerAtLarge } from './officer';
import { targetEnemy, targetEscaped, targetStillDormant } from './target';
import { targetsLeave } from './timedEffects';

type AlertRule = Extract<ObjectiveRule, { type: 'alertConsequence' }>;
export type ExpansionAlertEffect =
  Extract<AlertEffect, 'targetsLeave' | 'documentsBurned' | 'contactAborts' | 'targetFlees' | 'shootTheCaptive'>;

export function expansionStillApplies(ctx: Ctx, effect: ExpansionAlertEffect): boolean {
  const { world: w, statics } = ctx;
  const o = w.objective;
  switch (effect) {
    case 'targetsLeave':
      return targetProps(statics.def).some((t) => !propDestroyed(w, t.id));
    case 'documentsBurned':
      return !o.complete;
    case 'contactAborts':
      return o.captives.some((c) => c.state === 'absent' || c.state === 'waiting');
    case 'targetFlees': {
      if (o.targetGone || o.complete) return false;
      if (o.targetAboardId !== -1) return true;
      const e = targetEnemy(ctx);
      return e ? isFighter(e) : targetStillDormant(ctx);
    }
    case 'shootTheCaptive': {
      const atLarge = officerAtLarge(ctx);
      if (atLarge && isAlive(atLarge)) return true;
      return o.captives.some((c) => c.state === 'held' || c.state === 'following');
    }
  }
}

/** The nearest soldier who could do it: on his feet, carrying a weapon, close enough to be given the order. */
function nearestGuard(ctx: Ctx, victim: Enemy): Enemy | null {
  let best: Enemy | null = null;
  let bestD: number = EXPANSION.shootRadius;
  for (const e of ctx.world.enemies) {
    if (e.id === victim.id || !isFighter(e) || e.state === 'down' || isAutomated(e.kind) || e.unarmed === true) continue;
    const d = dist(e.x, e.z, victim.x, victim.z);
    if (d <= bestD) {
      bestD = d;
      best = e;
    }
  }
  return best;
}

function shootTheCaptive(ctx: Ctx, rule: AlertRule): void {
  const o = ctx.world.objective;
  // From here on the executioners' machinery hunts whoever the ranger is walking out with (executioners.ts).
  o.shootOrdered = true;
  const victim = officerAtLarge(ctx);
  if (!victim || !isAlive(victim)) return;
  const guard = nearestGuard(ctx, victim);
  if (!guard) return;
  // The round is for show - enemy rounds only ever strike the ranger - and the deed is settled here.
  const bearing = atan2(victim.x - guard.x, victim.z - guard.z);
  const reach = Math.max(0.5, dist(guard.x, guard.z, victim.x, victim.z) - 0.6);
  const dirX = sin(bearing);
  const dirZ = cos(bearing);
  spawnBullet(ctx, false, guard.x + dirX * 0.6, guard.z + dirZ * 0.6, dirX, dirZ, reach, -1, false);
  guard.sinceShot = 0;
  makeNoise(ctx, guard.x, guard.z, NOISE.enemyShot, 'gunfire', guard.id);
  damageEnemy(ctx, victim, 99, false);
  failObjective(ctx, rule.reason ?? `${targetName(ctx.statics.def, 'THE PRISONER')} WAS SHOT BY HIS OWN GUARDS`);
}

export function fireExpansionEffect(ctx: Ctx, rule: AlertRule, effect: ExpansionAlertEffect): void {
  const o = ctx.world.objective;
  // R4: an alerted garrison puts things on the map as a timed event does - the launchers that drive off are
  // the launchers that were parked, and the general's car comes for him.
  wakeTagged(ctx, rule.wakes ?? effect);
  switch (effect) {
    case 'targetsLeave':
      targetsLeave(ctx, rule.reason);
      break;
    case 'documentsBurned':
      failObjective(ctx, rule.reason ?? 'THE DOCUMENTS WERE BURNED');
      break;
    case 'contactAborts':
      for (const c of o.captives) if (c.state === 'absent' || c.state === 'waiting') loseCaptive(ctx, c);
      failObjective(ctx, rule.reason ?? 'THE CONTACT DID NOT COME');
      break;
    case 'targetFlees': {
      // R4: riding, he is driven back the way he came, and is gone at the end of it (target.ts `rideAway`).
      const car = ctx.world.enemies.find((v) => v.id === o.targetAboardId)?.vehicle;
      if (car) {
        car.reversing = true;
        o.targetFleeing = true;
        break;
      }
      // On the map he runs for it (target.ts); still to come, he simply never does.
      if (targetEnemy(ctx)) o.targetFleeing = true;
      else targetEscaped(ctx);
      break;
    }
    case 'shootTheCaptive':
      shootTheCaptive(ctx, rule);
      break;
  }
}
